import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Tabs = [
    { id: 'start', label: '시작하기', icon: 'fas fa-rocket' },
    { id: 'learn', label: '학습 진행', icon: 'fas fa-graduation-cap' },
    { id: 'troubleshoot', label: '문제 해결', icon: 'fas fa-wrench' },
    { id: 'roadmap', label: '로드맵', icon: 'fas fa-map' },
    { id: 'dashboard', label: '대시보드', icon: 'fas fa-chart-bar' },
];

export default function LunaGuideModal({ isOpen, onClose }) {
    const [activeTab, setActiveTab] = useState('start');

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <AnimatePresence>
            <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-md"
                onClick={onClose}
            />

            <motion.div
                key="modal-content"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none"
            >
                <div
                    className="w-full max-w-7xl bg-[#0f1219] border border-white/10 rounded-2xl shadow-2xl flex flex-col pointer-events-auto h-[850px] max-h-[95vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-gradient-to-r from-[#0f1219] to-[#1e293b] shrink-0">
                        <div>
                            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
                                LUNA 활용 가이드 🚀
                            </h2>
                            <p className="text-slate-400 text-xs mt-0.5">AI 코딩 튜터를 200% 활용하는 방법</p>
                        </div>
                        <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    <div className="flex flex-1 overflow-hidden">
                        {/* Sidebar */}
                        <div className="w-60 bg-black/20 border-r border-white/5 p-3 space-y-1 overflow-y-auto shrink-0 custom-scrollbar">
                            {Tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${activeTab === tab.id
                                        ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 shadow-[0_0_15px_rgba(34,211,238,0.1)]'
                                        : 'text-slate-400 hover:bg-white/5 hover:text-white'
                                        }`}
                                >
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${activeTab === tab.id ? 'bg-cyan-500/20' : 'bg-white/5'}`}>
                                        <i className={`${tab.icon} ${activeTab === tab.id ? 'text-lg' : 'text-sm'}`}></i>
                                    </div>
                                    <span className="font-bold text-sm tracking-wide">{tab.label}</span>
                                </button>
                            ))}
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-hidden bg-[#0f1219] relative">
                            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>
                            <div className="relative z-10 w-full h-full p-6">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={activeTab}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.2 }}
                                        className="w-full h-full"
                                    >
                                        {activeTab === 'start' && <StartContent />}
                                        {activeTab === 'learn' && <LearnContent />}
                                        {activeTab === 'troubleshoot' && <TroubleshootContent />}
                                        {activeTab === 'roadmap' && <RoadmapContent />}
                                        {activeTab === 'dashboard' && <DashboardContent />}
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>,
        document.body
    );
}

// ============================================
// Layout System (DevSetupModal 스타일)
// ============================================

function StepLayout({ title, accentColor, icon, steps }) {
    const [activeStep, setActiveStep] = useState(0);

    return (
        <div className="h-full flex flex-col">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3 shrink-0">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 shadow-lg">
                    <i className={`${icon} ${accentColor} text-xl`}></i>
                </div>
                {title}
            </h3>

            <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
                {/* Steps Column */}
                <div className="w-full lg:w-1/3 flex flex-col gap-3 overflow-y-auto custom-scrollbar pr-2 pb-10">
                    {steps.map((step, index) => (
                        <div
                            key={index}
                            onClick={() => setActiveStep(index)}
                            className={`p-4 rounded-xl border transition-all cursor-pointer relative overflow-hidden group ${activeStep === index
                                ? 'bg-white/10 border-white/20 shadow-lg ring-1 ring-white/10'
                                : 'bg-white/5 border-transparent hover:bg-white/10'
                                }`}
                        >
                            {activeStep === index && (
                                <div className={`absolute left-0 top-0 bottom-0 w-1 ${accentColor.replace('text-', 'bg-')}`}></div>
                            )}

                            <div className="flex items-start gap-4">
                                <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 mt-0.5 ${activeStep === index
                                    ? 'bg-white text-black'
                                    : 'bg-white/10 text-slate-500'
                                    }`}>
                                    {index + 1}
                                </span>
                                <div className="flex-1">
                                    <h4 className={`font-bold text-sm mb-1 transition-colors ${activeStep === index ? 'text-white' : 'text-slate-400 group-hover:text-slate-200'}`}>
                                        {step.title}
                                    </h4>
                                    {activeStep === index && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="space-y-3"
                                        >
                                            <p className="text-xs text-slate-400 leading-relaxed">
                                                {step.desc}
                                            </p>
                                            {step.tip && (
                                                <div className="bg-black/40 border border-white/5 rounded p-3 flex gap-2">
                                                    <i className="fas fa-lightbulb text-yellow-500 text-xs mt-0.5"></i>
                                                    <p className="text-[11px] text-slate-300 leading-normal">
                                                        <span className="font-bold text-yellow-500 mb-1 block">Tip</span>
                                                        {step.tip}
                                                    </p>
                                                </div>
                                            )}
                                        </motion.div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Visualizer Column */}
                <div className="flex-1 bg-[#151921] rounded-2xl border border-white/10 overflow-hidden shadow-2xl relative flex flex-col">
                    <div className="px-5 py-3 border-b border-white/5 bg-[#1a1f2b] flex items-center justify-between shrink-0">
                        <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-red-500/80"></span>
                            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></span>
                            <span className="w-2.5 h-2.5 rounded-full bg-green-500/80"></span>
                        </div>
                        <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
                            Step {activeStep + 1}
                        </div>
                    </div>

                    <div className="flex-1 relative overflow-hidden bg-[#0f1219]">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeStep}
                                initial={{ opacity: 0, scale: 0.98 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.98 }}
                                transition={{ duration: 0.2 }}
                                className="absolute inset-0 p-6 flex flex-col items-center justify-center overflow-y-auto custom-scrollbar"
                            >
                                {steps[activeStep].content}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============================================
// Visual Components (실제 앱 디자인 반영)
// ============================================

// 실제 ChatInterface의 채팅 버블 스타일 반영
function MockChatBubble({ isUser, children, delay = 0 }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay }}
            className={`flex gap-3 items-start ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
        >
            {/* Avatar - 실제 LunaAvatar/UserAvatar 스타일 */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${isUser
                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
                : 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white'
                }`}>
                {isUser ? 'U' : 'L'}
            </div>
            {/* Bubble - 실제 ChatMessage 스타일 */}
            <div className={`max-w-[80%] px-4 py-3 shadow-lg text-sm ${isUser
                ? 'bg-[#0f172a] text-white rounded-2xl rounded-tr-none'  // 실제 사용자 버블 색상
                : 'bg-[#251d3a] text-white rounded-2xl rounded-tl-none'  // 실제 AI 버블 색상
                }`}>
                {children}
            </div>
        </motion.div>
    );
}

// 실제 PathConsultation의 옵션 버튼 스타일 반영
function MockPathOption({ type, icon, title, desc, delay = 0 }) {
    const themes = {
        short: {
            bg: 'from-cyan-900/30 to-blue-900/20 hover:from-cyan-800/40 hover:to-blue-800/30',
            border: 'border-cyan-500/30 hover:border-cyan-400/50',
            iconBg: 'bg-cyan-500/20 text-cyan-400',
            badge: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/20',
            badgeText: '단기 완성'
        },
        medium: {
            bg: 'from-emerald-900/30 to-teal-900/20 hover:from-emerald-800/40 hover:to-teal-800/30',
            border: 'border-emerald-500/30 hover:border-emerald-400/50',
            iconBg: 'bg-emerald-500/20 text-emerald-400',
            badge: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/20',
            badgeText: '과목 마스터'
        },
        long: {
            bg: 'from-indigo-900/30 to-purple-900/20 hover:from-indigo-800/40 hover:to-purple-800/30',
            border: 'border-indigo-500/30 hover:border-indigo-400/50',
            iconBg: 'bg-indigo-500/20 text-indigo-400',
            badge: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/20',
            badgeText: '커리어 로드맵'
        }
    };
    const theme = themes[type] || themes.short;

    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay }}
            className={`w-full group flex items-start gap-4 p-4 rounded-xl transition-all cursor-pointer bg-gradient-to-r ${theme.bg} border ${theme.border}`}
        >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${theme.iconBg}`}>
                <i className={`${icon} text-xl`}></i>
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${theme.badge}`}>
                        {theme.badgeText}
                    </span>
                </div>
                <h4 className="text-sm font-bold text-white">{title}</h4>
                <p className="text-xs text-slate-400 leading-relaxed mt-1">{desc}</p>
            </div>
        </motion.div>
    );
}

// 실제 Sidebar의 TimelineItem 스타일 반영
function MockTimelineItem({ step, index, isActive, isCompleted, delay = 0 }) {
    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay }}
            className={`
                py-2.5 px-3 rounded-md cursor-pointer transition-all duration-200 border-l-2
                ${isActive
                    ? 'bg-slate-800/50 border-cyan-500 text-slate-200'
                    : isCompleted
                        ? 'bg-transparent border-transparent text-slate-400'
                        : 'bg-transparent border-transparent opacity-40'
                }
            `}
        >
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                    {isActive ? (
                        <div className="w-2 h-2 rounded-full bg-cyan-500 shadow-[0_0_8px_rgba(6,182,212,0.8)]"></div>
                    ) : isCompleted ? (
                        <i className="fas fa-check text-emerald-500 text-xs"></i>
                    ) : (
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-700"></div>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium leading-snug ${isActive ? 'text-slate-200' : isCompleted ? 'text-slate-400' : 'text-slate-500'}`}>
                        {step}
                    </p>
                    {isActive && (
                        <span className="text-[10px] text-cyan-500/80 font-mono mt-0.5 block">
                            STEP {index + 1}
                        </span>
                    )}
                </div>
            </div>
        </motion.div>
    );
}

// 실제 Sidebar 스타일의 커리큘럼 프리뷰
function MockSidebar({ steps, activeStep = 0 }) {
    return (
        <div className="w-full max-w-xs bg-black/40 rounded-xl border border-white/5 overflow-hidden">
            {/* 실제 Sidebar 헤더 스타일 */}
            <div className="px-4 py-3 border-b border-white/5 bg-white/5">
                <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-sm font-bold text-slate-200 leading-tight">파이썬 기초</h3>
                    <span className="shrink-0 text-[10px] text-slate-500 font-medium">
                        <span className="text-slate-400 font-bold">{steps.length}</span> Steps
                    </span>
                </div>
                {/* 진행률 바 */}
                <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden mt-2">
                    <div className="h-full bg-cyan-600 rounded-full transition-all duration-500" style={{ width: `${(activeStep / steps.length) * 100}%` }}></div>
                </div>
            </div>
            {/* 타임라인 */}
            <div className="p-3 space-y-1">
                {steps.map((step, idx) => (
                    <MockTimelineItem
                        key={idx}
                        step={step}
                        index={idx}
                        isActive={idx === activeStep}
                        isCompleted={idx < activeStep}
                        delay={idx * 0.05}
                    />
                ))}
            </div>
        </div>
    );
}

// ============================================
// Tab Contents
// ============================================

function StartContent() {
    const steps = [
        {
            title: "채팅창에 학습 주제 입력",
            desc: "좌측 채팅창에 배우고 싶은 것을 자유롭게 말해주세요.",
            tip: "구체적으로 말할수록 더 정확한 학습 경로가 만들어집니다.",
            content: (
                <div className="space-y-4 w-full max-w-sm">
                    <MockChatBubble isUser={true}>
                        파이썬 기초 배우고 싶어
                    </MockChatBubble>
                    <MockChatBubble isUser={false} delay={0.3}>
                        좋아요! 학습 방향을 고민해봤어요. 어떤 방식으로 시작할까요? ✨
                    </MockChatBubble>
                </div>
            )
        },
        {
            title: "학습 경로 선택",
            desc: "목표에 맞는 3가지 학습 경로 중 하나를 선택하세요.",
            content: (
                <div className="w-full max-w-md bg-slate-800/80 rounded-2xl border border-slate-700/50 overflow-hidden shadow-xl">
                    {/* 실제 PathConsultation 헤더 */}
                    <div className="p-4 border-b border-slate-700/50 bg-slate-900/40">
                        <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0 text-sm">🌙</div>
                            <p className="text-sm text-slate-200 leading-relaxed">학습 방향을 고민해봤어요. 어떤 방식으로 시작할까요?</p>
                        </div>
                    </div>
                    <div className="p-4 space-y-3">
                        <h5 className="text-xs font-semibold text-cyan-400/80 px-1">단기 학습 추천</h5>
                        <MockPathOption type="short" icon="fas fa-book" title="파이썬 핵심 문법" desc="2주 완성, 핵심만 빠르게" delay={0} />
                        <h5 className="text-xs font-semibold text-emerald-400/80 px-1 pt-2 border-t border-slate-700/50">중기 과목 마스터</h5>
                        <MockPathOption type="medium" icon="fas fa-graduation-cap" title="파이썬 마스터 코스" desc="실전 프로젝트 포함, 체계적 학습" delay={0.1} />
                        <h5 className="text-xs font-semibold text-indigo-400/80 px-1 pt-2 border-t border-slate-700/50">장기 커리어 로드맵</h5>
                        <MockPathOption type="long" icon="fas fa-rocket" title="백엔드 개발자 로드맵" desc="3~6개월 커리어 완성 과정" delay={0.2} />
                    </div>
                </div>
            )
        },
        {
            title: "토픽 선택 → 커리큘럼 생성",
            desc: "학습 경로를 클릭하면 토픽이 생성됩니다. 토픽 클릭 시 상세 커리큘럼이 만들어집니다.",
            content: (
                <div className="flex items-center gap-4 text-center">
                    {[
                        { icon: 'fas fa-comments', label: '주제 입력', color: 'cyan' },
                        { icon: 'fas fa-route', label: '경로 선택', color: 'emerald' },
                        { icon: 'fas fa-layer-group', label: '토픽 생성', color: 'purple' },
                        { icon: 'fas fa-list-check', label: '커리큘럼', color: 'pink' },
                    ].map((item, idx) => (
                        <React.Fragment key={idx}>
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: idx * 0.1 }}
                                className="flex flex-col items-center gap-2"
                            >
                                <div className={`w-14 h-14 rounded-xl flex items-center justify-center bg-${item.color}-500/20 border border-${item.color}-500/30`}>
                                    <i className={`${item.icon} text-xl text-${item.color}-400`}></i>
                                </div>
                                <span className="text-[10px] font-bold text-white">{item.label}</span>
                            </motion.div>
                            {idx < 3 && <i className="fas fa-chevron-right text-slate-600 text-lg"></i>}
                        </React.Fragment>
                    ))}
                </div>
            )
        },
        {
            title: "레슨 시작!",
            desc: "커리큘럼에서 단원을 클릭하면 레슨이 시작됩니다.",
            tip: "로드맵을 교체하려면 상단의 '새 주제' 버튼을 클릭하세요!",
            content: (
                <MockSidebar
                    steps={['변수와 자료형', '조건문 if/else', '반복문 for/while', '함수 정의하기', '클래스와 객체']}
                    activeStep={1}
                />
            )
        }
    ];

    return <StepLayout title="LUNA 시작하기" accentColor="text-cyan-400" icon="fas fa-rocket" steps={steps} />;
}

function LearnContent() {
    const steps = [
        {
            title: "개념 학습",
            desc: "화면 오른쪽에서 핵심 개념과 설명을 학습합니다.",
            content: (
                <div className="bg-[#1e293b]/50 rounded-xl border border-white/10 p-6 max-w-md w-full">
                    <div className="flex items-center gap-2 mb-4">
                        <i className="fas fa-book-open text-blue-400"></i>
                        <span className="text-sm font-bold text-white">개념 설명</span>
                    </div>
                    <div className="space-y-3 text-sm text-slate-300">
                        <p><strong className="text-white">변수(Variable)</strong>란 값을 저장하는 메모리 공간입니다.</p>
                        {/* 실제 ChatMessage의 코드블록 스타일 */}
                        <div className="ide-code-block rounded-lg overflow-hidden !bg-[#1e1e1e] border border-white/10 shadow-lg">
                            <div className="ide-header flex items-center px-3 py-1.5 !bg-[#2d2d2d] border-b border-black/20">
                                <div className="ide-dots flex gap-1.5">
                                    <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]"></span>
                                    <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]"></span>
                                    <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]"></span>
                                </div>
                                <span className="ide-title text-slate-400 text-xs ml-2 font-mono opacity-80">main.py</span>
                            </div>
                            <div className="p-3 font-mono text-sm">
                                <span className="text-pink-400">name</span> <span className="text-white">=</span> <span className="text-emerald-400">"LUNA"</span>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "문제 풀이",
            desc: "코드 에디터에서 직접 코드를 작성합니다. AI가 실시간으로 분석합니다.",
            content: (
                <div className="bg-[#1e1e1e] rounded-xl border border-white/10 overflow-hidden max-w-md w-full shadow-lg">
                    <div className="bg-[#2d2d2d] px-4 py-2 border-b border-black/20 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="flex gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]"></span>
                                <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]"></span>
                                <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]"></span>
                            </div>
                            <span className="text-xs text-slate-400 font-mono ml-2">solution.py</span>
                        </div>
                        <button className="px-3 py-1 bg-emerald-500 text-black text-xs font-bold rounded">채점하기</button>
                    </div>
                    <div className="p-4 font-mono text-sm space-y-1">
                        <div><span className="text-slate-500 select-none mr-4">1</span><span className="text-pink-400">name</span> <span className="text-white">=</span> <span className="text-emerald-400">"______"</span></div>
                        <div><span className="text-slate-500 select-none mr-4">2</span><span className="text-blue-400">print</span><span className="text-white">(name)</span></div>
                    </div>
                </div>
            )
        },
        {
            title: "AI 피드백",
            desc: "채점하기 버튼을 누르면 AI가 코드를 분석하고 피드백을 제공합니다.",
            tip: "막히는 부분은 채팅창에 질문해보세요!",
            content: (
                <div className="space-y-4 max-w-md w-full">
                    <motion.div initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <i className="fas fa-check-circle text-emerald-400"></i>
                            <span className="text-sm font-bold text-emerald-400">정답입니다! 🎉</span>
                        </div>
                        <p className="text-xs text-slate-300">변수에 문자열을 잘 할당했어요!</p>
                    </motion.div>
                    <MockChatBubble isUser={false} delay={0.2}>
                        <div className="flex items-center gap-2 mb-2">
                            <i className="fas fa-lightbulb text-cyan-400"></i>
                            <span className="font-bold text-cyan-300">추가 팁</span>
                        </div>
                        f-string을 사용하면 더 깔끔하게 출력할 수 있어요!
                    </MockChatBubble>
                </div>
            )
        },
        {
            title: "다음 단원으로!",
            desc: "레슨을 완료하면 XP를 획득하고 다음 단원으로 이동합니다.",
            content: (
                <div className="text-center space-y-6">
                    {/* 실제 LessonCompletionCard 스타일 반영 */}
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="relative overflow-hidden rounded-3xl p-[1px] inline-block"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/60 via-purple-500/60 to-pink-500/60 opacity-80"></div>
                        <div className="relative bg-[#0f111a] rounded-[23px] px-8 py-6">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-[0_0_30px_rgba(74,222,128,0.4)]">
                                <i className="fas fa-check text-2xl text-white"></i>
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">단원 학습 완료!</h3>
                            <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-500/30 rounded-xl px-4 py-2 mb-4">
                                <i className="fas fa-trophy text-yellow-400"></i>
                                <span className="text-lg font-bold text-white">+50 XP</span>
                            </div>
                        </div>
                    </motion.div>
                    <motion.button
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="px-6 py-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white font-bold rounded-xl flex items-center gap-2 mx-auto shadow-[0_4px_20px_rgba(99,102,241,0.3)]"
                    >
                        다음 단원 시작 <i className="fas fa-arrow-right"></i>
                    </motion.button>
                </div>
            )
        }
    ];

    return <StepLayout title="학습 진행 방법" accentColor="text-purple-400" icon="fas fa-graduation-cap" steps={steps} />;
}

function TroubleshootContent() {
    const steps = [
        {
            title: "레슨이 생성되지 않아요",
            desc: "네트워크 문제 또는 일시적인 오류일 수 있어요.",
            tip: "사이드바에서 원하는 단원을 직접 클릭하면 바로 이동할 수 있어요!",
            content: (
                <div className="space-y-4 w-full max-w-md">
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <i className="fas fa-exclamation-triangle text-amber-400"></i>
                            <span className="text-sm font-bold text-amber-400">해결책</span>
                        </div>
                        <div className="space-y-2 text-sm text-slate-300">
                            <div className="flex items-center gap-2">
                                <i className="fas fa-hand-pointer text-cyan-400"></i>
                                <span>사이드바에서 <strong className="text-cyan-400">단원 직접 클릭</strong></span>
                            </div>
                            <div className="flex items-center gap-2">
                                <i className="fas fa-sync text-cyan-400"></i>
                                <span>페이지 새로고침 (F5)</span>
                            </div>
                        </div>
                    </div>
                    <MockSidebar steps={['변수의 이해', '조건문', '반복문']} activeStep={0} />
                </div>
            )
        },
        {
            title: "다음단원 버튼이 안 보여요",
            desc: "레슨 완료 처리가 안 됐을 수 있어요.",
            content: (
                <div className="space-y-4 w-full max-w-md">
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <i className="fas fa-lightbulb text-emerald-400"></i>
                            <span className="text-sm font-bold text-emerald-400">걱정 마세요!</span>
                        </div>
                        <p className="text-sm text-slate-300">
                            사이드바의 단원 목록은 <strong className="text-white">언제든지 자유롭게</strong> 선택할 수 있어요.
                        </p>
                    </div>
                    <div className="flex items-center justify-center gap-4">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-xl bg-slate-700/50 flex items-center justify-center mb-2 border-2 border-dashed border-slate-600">
                                <i className="fas fa-times text-2xl text-slate-500"></i>
                            </div>
                            <span className="text-xs text-slate-500">버튼 없음</span>
                        </div>
                        <i className="fas fa-arrow-right text-slate-600"></i>
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-xl bg-cyan-500/20 flex items-center justify-center mb-2 border border-cyan-500/30">
                                <i className="fas fa-list text-2xl text-cyan-400"></i>
                            </div>
                            <span className="text-xs text-cyan-400">사이드바 클릭</span>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "로컬에서 직접 코딩하고 싶어요",
            desc: "VS Code, Python 등 실제 개발 환경을 설정할 수 있어요.",
            content: (
                <div className="text-center space-y-4">
                    <div className="bg-white/5 border border-white/10 rounded-xl p-6 max-w-sm mx-auto">
                        <i className="fas fa-terminal text-4xl text-slate-400 mb-4"></i>
                        <h4 className="font-bold text-white mb-2">개발환경 세팅 가이드</h4>
                        <p className="text-sm text-slate-400 mb-4">상단의 "개발환경 세팅" 버튼을 클릭하세요!</p>
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-900 rounded-full text-sm font-bold">
                            <i className="fas fa-terminal text-xs"></i>
                            개발환경 세팅
                        </div>
                    </div>
                </div>
            )
        }
    ];

    return <StepLayout title="문제 해결" accentColor="text-amber-400" icon="fas fa-wrench" steps={steps} />;
}

function RoadmapContent() {
    const steps = [
        {
            title: "로드맵 생성하기",
            desc: "상단의 '로드맵' 메뉴 클릭 → 목표 입력 → AI가 맞춤형 커리어 로드맵을 설계합니다.",
            content: (
                <div className="w-full max-w-md flex flex-col items-center">
                    {/* Hero Icon */}
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="w-20 h-20 mb-6 relative group"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-2xl blur-xl opacity-60 animate-pulse-slow"></div>
                        <div className="relative w-full h-full bg-[#0f1115] border border-white/10 rounded-2xl flex items-center justify-center">
                            <span className="text-4xl">🗺️</span>
                        </div>
                    </motion.div>

                    <h4 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-slate-400 mb-2">
                        Design Your Career Path
                    </h4>
                    <p className="text-xs text-slate-400 mb-6 text-center max-w-xs">
                        <span className="text-cyan-400">AI Architect</span>가 당신만을 위한<br />마스터 플랜을 설계합니다.
                    </p>

                    {/* Input Simulation */}
                    <div className="w-full relative group mb-6">
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl opacity-30 blur"></div>
                        <div className="relative w-full bg-[#0f1115] border border-slate-700/50 rounded-xl px-4 py-3 flex items-center justify-between">
                            <span className="text-sm text-white">Full Stack Developer</span>
                            <span className="w-1.5 h-5 bg-cyan-500 animate-pulse"></span>
                        </div>
                    </div>

                    {/* Tags */}
                    <div className="flex gap-2 justify-center flex-wrap">
                        {['Python', 'React', 'Data Science'].map(tag => (
                            <span key={tag} className="px-2 py-1 rounded-md bg-slate-800/50 border border-slate-700 text-[10px] text-slate-400">
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>
            )
        },
        {
            title: "Phase 구조 (마스터 플랜)",
            desc: "로드맵은 여러 Phase로 구성되며, 단계별로 체계적인 학습이 가능합니다.",
            content: (
                <div className="w-full max-w-md relative pl-4">
                    {/* Beam Line */}
                    <div className="absolute left-[27px] top-4 bottom-0 w-[2px] bg-slate-800/50">
                        <div className="w-full h-1/2 bg-gradient-to-b from-cyan-500 via-blue-500 to-transparent"></div>
                    </div>

                    <div className="space-y-4">
                        {[
                            { title: 'Python 기초', desc: '프로그래밍의 핵심 개념', topics: 4, status: 'active', icon: 'fa-python' },
                            { title: '데이터 구조', desc: '효율적인 데이터 관리', topics: 5, status: 'locked', icon: 'fa-database' },
                            { title: '알고리즘', desc: '문제 해결 능력 파워업', topics: 6, status: 'locked', icon: 'fa-brain' },
                        ].map((phase, idx) => (
                            <motion.div
                                key={phase.title}
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: idx * 0.1 }}
                                className="relative flex items-center gap-4"
                            >
                                {/* Phase Icon */}
                                <div className={`
                                relative z-10 w-12 h-12 rounded-xl flex items-center justify-center text-lg shrink-0 border
                                ${phase.status === 'active'
                                        ? 'bg-[#0f1115] border-cyan-500 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.2)]'
                                        : 'bg-[#0f1115] border-slate-800 text-slate-600 grayscale'}
                            `}>
                                    <i className={`fas ${phase.icon}`}></i>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-px rounded border ${phase.status === 'active'
                                            ? 'bg-cyan-950/30 text-cyan-400 border-cyan-500/20'
                                            : 'bg-slate-800 text-slate-500 border-slate-700'
                                            }`}>Phase {idx + 1}</span>
                                    </div>
                                    <h4 className={`text-sm font-bold ${phase.status === 'active' ? 'text-white' : 'text-slate-500'}`}>{phase.title}</h4>
                                    <p className="text-[10px] text-slate-500 truncate">{phase.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            )
        },
        {
            title: "토픽 언락 시스템",
            desc: "앞선 토픽을 완료해야 다음 토픽이 열립니다. 게임처럼 하나씩 정복해보세요!",
            content: (
                <div className="w-full max-w-md">
                    <div className="grid grid-cols-1 gap-3">
                        {[
                            { title: '변수와 자료형', desc: '데이터를 저장하는 방법', status: 'completed' },
                            { title: '조건문 (if/else)', desc: '상황에 따라 다르게 동작하기', status: 'active' },
                            { title: '반복문 (for/while)', desc: '반복적인 작업 자동화', status: 'locked' },
                        ].map((topic, idx) => (
                            <motion.div
                                key={topic.title}
                                initial={{ y: 10, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: idx * 0.1 }}
                                className={`
                                    p-3 rounded-xl border transition-all flex items-center gap-3
                                    ${topic.status === 'active'
                                        ? 'bg-[#0f1115] border-cyan-500/30 shadow-[0_4px_12px_rgba(0,0,0,0.3)]'
                                        : topic.status === 'completed'
                                            ? 'bg-emerald-900/10 border-emerald-500/20 opacity-70'
                                            : 'bg-[#0f1115] border-slate-800 opacity-40'}
                                `}
                            >
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0
                                    ${topic.status === 'active' ? 'bg-cyan-500/20 text-cyan-400' :
                                        topic.status === 'completed' ? 'bg-emerald-500/20 text-emerald-500' :
                                            'bg-slate-800 text-slate-600'}
                                `}>
                                    {topic.status === 'completed' ? <i className="fas fa-check"></i> :
                                        topic.status === 'active' ? <i className="fas fa-play"></i> :
                                            <i className="fas fa-lock"></i>}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className={`text-sm font-bold ${topic.status === 'active' ? 'text-white' : 'text-slate-500'}`}>{topic.title}</h4>
                                    <p className="text-[10px] text-slate-500 truncate">{topic.desc}</p>
                                </div>
                                {topic.status === 'active' && (
                                    <div className="px-2 py-1 rounded bg-cyan-500/10 border border-cyan-500/20 text-[10px] text-cyan-400 font-bold animate-pulse">
                                        START
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            )
        }
    ];

    return <StepLayout title="로드맵 활용하기" accentColor="text-purple-400" icon="fas fa-map" steps={steps} />;
}

function DashboardContent() {
    const steps = [
        {
            title: "대시보드 접속",
            desc: "상단의 '대시보드' 메뉴를 클릭하면 학습 현황을 한눈에 확인할 수 있어요.",
            content: (
                <div className="text-center space-y-4">
                    {/* Header.jsx 네비게이션 스타일과 통일 */}
                    <div className="inline-flex items-center gap-2 bg-[#0B0E14]/80 rounded-xl px-4 py-2.5 border border-white/10">
                        <button className="relative px-3 py-2 text-sm font-medium text-white transition-all">
                            <span>대시보드</span>
                            {/* 활성 탭 하단 밑줄 - Header.jsx 스타일 */}
                            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-cyan-500 rounded-full"></div>
                        </button>
                        <button className="px-3 py-2 text-sm font-medium text-slate-400 hover:text-slate-200 transition-all">
                            로드맵
                        </button>
                        <button className="px-3 py-2 text-sm font-medium text-slate-400 hover:text-slate-200 transition-all">
                            랭킹
                        </button>
                    </div>
                    <p className="text-xs text-slate-500">↑ 상단 네비게이션에서 클릭</p>
                </div>
            )
        },
        {
            title: "XP & 레벨 확인",
            desc: "획득한 XP, 현재 레벨, 연속 학습일수를 확인할 수 있어요.",
            content: (
                <div className="w-full max-w-md">
                    {/* 실제 DashboardPage Quick Stats 스타일 */}
                    <div className="flex gap-4 bg-white/5 backdrop-blur-sm border border-white/5 rounded-xl p-3">
                        <div className="flex-1 text-center border-r border-white/10">
                            <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">XP Earned</div>
                            <div className="text-xl font-bold text-white font-mono">2,450</div>
                        </div>
                        <div className="flex-1 text-center border-r border-white/10">
                            <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">Streak Day</div>
                            <div className="text-xl font-bold text-orange-400 font-mono">7</div>
                        </div>
                        <div className="flex-1 text-center">
                            <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">Level</div>
                            <div className="text-xl font-bold text-purple-400 font-mono">12</div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "학습 이력 & 통계",
            desc: "풀었던 문제, 활동 로그, AI 인사이트 등을 체계적으로 관리할 수 있어요.",
            content: (
                <div className="grid grid-cols-2 gap-3 w-full max-w-md">
                    {/* 실제 DashboardPage StatCard 스타일 */}
                    {[
                        { icon: 'fas fa-code', color: 'cyan', title: 'Total Solved', value: '128' },
                        { icon: 'fas fa-bullseye', color: 'green', title: 'Success Rate', value: '87%' },
                        { icon: 'fas fa-brain', color: 'purple', title: 'AI Insights', value: '15' },
                        { icon: 'fas fa-history', color: 'orange', title: 'System Logs', value: '42' },
                    ].map((stat, idx) => (
                        <motion.div
                            key={stat.title}
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: idx * 0.1 }}
                            className="relative overflow-hidden rounded-xl bg-white/5 border border-white/10 p-4 group"
                        >
                            <div className={`absolute top-0 right-0 w-16 h-16 bg-${stat.color}-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2`}></div>
                            <div className="relative z-10">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[10px] font-bold tracking-wider text-slate-500 uppercase">{stat.title}</span>
                                    <div className={`w-8 h-8 rounded-lg bg-${stat.color}-500/20 flex items-center justify-center text-${stat.color}-400`}>
                                        <i className={stat.icon}></i>
                                    </div>
                                </div>
                                <div className="text-2xl font-bold text-white font-mono">{stat.value}</div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )
        },
        {
            title: "진도 관리",
            desc: "로드맵 진행률, 언어별 문제 분포, 획득한 뱃지 등을 확인할 수 있어요.",
            tip: "꾸준한 학습으로 연속 학습일수를 늘려보세요!",
            content: (
                <div className="w-full max-w-md space-y-4">
                    {/* 실제 DashboardPage Roadmap Progress 스타일 */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                                <i className="fas fa-map text-cyan-400 text-sm"></i>
                            </div>
                            <h4 className="text-sm font-bold text-white">Career Roadmap</h4>
                        </div>
                        <div className="flex justify-between text-xs text-slate-400 mb-2">
                            <span>Progress</span>
                            <span className="font-mono">35%</span>
                        </div>
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: '35%' }}
                                transition={{ duration: 1 }}
                                className="h-full bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600"
                            ></motion.div>
                        </div>
                    </div>
                    {/* 언어별 분포 */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                        <h4 className="text-[10px] font-bold text-slate-500 uppercase mb-3">Language Distribution</h4>
                        <div className="space-y-2">
                            {['Python', 'JavaScript', 'SQL'].map((lang, idx) => (
                                <div key={lang} className="flex items-center gap-3">
                                    <span className="w-16 text-xs font-bold text-slate-300">{lang}</span>
                                    <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full ${idx === 0 ? 'bg-cyan-400' : idx === 1 ? 'bg-yellow-400' : 'bg-purple-400'}`} style={{ width: `${70 - idx * 20}%` }}></div>
                                    </div>
                                    <span className="text-xs text-slate-500 font-mono w-8 text-right">{70 - idx * 20}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )
        }
    ];

    return <StepLayout title="대시보드 활용하기" accentColor="text-cyan-400" icon="fas fa-chart-bar" steps={steps} />;
}
